import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Typography,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import RefreshIcon from '@material-ui/icons/Refresh';
import DeleteIcon from '@material-ui/icons/Delete';
import GetAppIcon from '@material-ui/icons/GetApp';
import { v4 as uuidv4 } from 'uuid';
import { DataTable } from 'src/components';
import SingleDatePicker from 'src/components/SingleDatePicker';
import { useNotif, useRequest } from 'src/hooks';
import { dashboard as dashboardEndpoints } from 'src/lib/endpoints';
import numeral from 'numeral';
import { exportCsvV2 } from 'src/utils';
import AddPageDialog from './AddPageDialog';
import {
  asObject,
  formatDateInput,
  isIsoDate,
  parseDateInput,
  toDateKey,
  parseDateKeyToNumeric,
  todayIso
} from 'src/utils/dashboardKVSUtils';

const MAX_ID_FIELDS = 10;
const ALLOW_ZERO_FIELDS = [
  'allow_zero_id',
  'allow_zero_id1',
  'allow_zero_id2',
  'allow_zero_id3',
  'allow_zero_id4',
  'allow_zero_id5',
  'allow_zero_id6',
  'allow_zero_id7',
  'allow_zero_id8',
  'allow_zero_id9',
  'allow_zero_id10',
  'allow_zero_idx'
];

const HOUR_RANGE = Array.from({ length: 24 }, (_, index) => index + 1);
const SUB_ENDPOINTS = ['/api/lib/sub', '/lib/sub'];

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2)
  },
  headerLeft: {
    flex: 1
  },
  headerRight: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center'
  },
  dateControls: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginTop: theme.spacing(1)
  },
  headerActions: {
    display: 'flex',
    gap: theme.spacing(1)
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  }
}));

const buildFetchPayload = data => {
  const payload = {};

  ALLOW_ZERO_FIELDS.forEach(key => {
    if (typeof data?.[key] !== 'undefined') {
      payload[key] = data[key];
    }
  });

  return {
    ...payload,
    ...asObject(data?.requestPayload),
    ...asObject(data?.fetchPayload)
  };
};

const buildSavePayloadExtras = data => ({
  ...asObject(data?.requestPayload),
  ...asObject(data?.savePayload)
});

const buildIdFieldConfig = data => {
  const fields = [];

  for (let idx = 1; idx <= MAX_ID_FIELDS; idx += 1) {
    const key = `id${idx}`;
    if (!data?.[key]) continue;

    fields.push({
      key,
      label: data[key]?.label || key,
      required: data[key]?.required ?? false,
      placeholder: data[key]?._format || '',
      type: data[key]?.type || 'number'
    });
  }

  if (!fields.length) {
    return Array.from({ length: 3 }, (_, index) => ({
      key: `id${index + 1}`,
      label: `ID ${index + 1}`,
      required: index === 0,
      placeholder: '',
      type: 'number'
    }));
  }

  return fields;
};

// helpers moved to utils/dashboardKVSUtils.js

const DashboardKVSField = ({ data = {} }) => {
  const classes = useStyles();
  const notify = useNotif();
  const request = useRequest();

  const kvsKey = data?.fld?.trim();
  const ixDashboard = data?.ixDashboard;
  const dataSignature = useMemo(() => JSON.stringify(data || {}), [data]);
  const idFields = useMemo(() => buildIdFieldConfig(data), [dataSignature]);
  const fetchExtras = useMemo(() => buildFetchPayload(data), [dataSignature]);
  const saveExtras = useMemo(() => buildSavePayloadExtras(data), [
    dataSignature
  ]);

  // Dynamic hour range based on configuration
  const hourRange = useMemo(() => {
    const hourConfig = data?.id3;
    if (hourConfig?.range && Array.isArray(hourConfig.range)) {
      return hourConfig.range;
    }
    if (hourConfig?.start && hourConfig?.end) {
      const start = Number(hourConfig.start);
      const end = Number(hourConfig.end);
      if (!Number.isNaN(start) && !Number.isNaN(end) && start <= end) {
        return Array.from(
          { length: end - start + 1 },
          (_, index) => start + index
        );
      }
    }
    // Default to 1-24 if no configuration found
    return HOUR_RANGE;
  }, [dataSignature]);
  const pageFetchPayload = useMemo(
    () => ({
      ixSubType:
        data?.pageSubType ||
        data?.id1?.ixSubType ||
        data?.id1?.ixSubtype ||
        data?.ixSubType ||
        data?.ixSubtype ||
        0,
      ...asObject(data?.pageFetchPayload)
    }),
    [dataSignature]
  );
  const fetchSignature = useMemo(
    () =>
      JSON.stringify({
        ixDashboard,
        kvsKey,
        fetchExtras
      }),
    [ixDashboard, kvsKey, fetchExtras]
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const lastFetchSignature = useRef(null);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(todayIso());
  const [pageOptions, setPageOptions] = useState([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [pendingPagesByDate, setPendingPagesByDate] = useState({});
  const [dialogPageIds, setDialogPageIds] = useState([]);
  const [dialogError, setDialogError] = useState('');
  const freezePane = Boolean(data?.sticky || data?.id1?.sticky);

  const canOperate = Boolean(ixDashboard && kvsKey);

  const parseEntryRows = useCallback(
    entry => {
      if (!entry) return [];

      const toRow = (source = {}) => {
        const layeredSources = [
          source && typeof source === 'object' ? source : null,
          entry?.value &&
          typeof entry.value === 'object' &&
          !Array.isArray(entry.value)
            ? entry.value
            : null,
          entry
        ].filter(Boolean);

        const resolveField = key => {
          for (const src of layeredSources) {
            if (
              src &&
              typeof src === 'object' &&
              !Array.isArray(src) &&
              Object.prototype.hasOwnProperty.call(src, key)
            ) {
              return src[key];
            }
          }
          return undefined;
        };

        const resolvedValue = resolveField('value');
        const primitiveValue =
          resolvedValue !== undefined
            ? resolvedValue
            : typeof source === 'number'
            ? source
            : typeof entry?.value === 'number'
            ? entry.value
            : entry?.value ?? '';

        const rowFieldValues = idFields.reduce((acc, field) => {
          const resolved = resolveField(field.key);
          return {
            ...acc,
            [field.key]: resolved !== undefined ? resolved : ''
          };
        }, {});

        const hasData =
          (primitiveValue !== undefined && primitiveValue !== '') ||
          Object.values(rowFieldValues).some(val => val !== '');

        if (!hasData) return null;

        return {
          tempId: uuidv4(),
          key: kvsKey,
          isPersisted: true,
          value: primitiveValue,
          id: resolveField('id') ?? entry?.id,
          ...rowFieldValues
        };
      };

      const rowsFromArray = sourceArray =>
        sourceArray.map(item => toRow(item)).filter(Boolean);

      if (Array.isArray(entry?.value?.items)) {
        return rowsFromArray(entry.value.items);
      }

      if (Array.isArray(entry?.value)) {
        return rowsFromArray(entry.value);
      }

      const baseSource =
        entry?.value &&
        typeof entry.value === 'object' &&
        !Array.isArray(entry.value)
          ? entry.value
          : entry;

      const singleRow = toRow(baseSource);
      return singleRow ? [singleRow] : [];
    },
    [idFields, kvsKey]
  );

  const fetchRows = useCallback(async () => {
    if (!canOperate) return;

    setLoading(true);
    try {
      const payload = {
        keys: [kvsKey],
        ...fetchExtras
      };

      const response = await request.post(
        dashboardEndpoints.kvsItems(ixDashboard),
        payload
      );

      if (!response.success) {
        notify.error(response.errorMessage || 'Failed to fetch dashboard KVS');
        return;
      }

      const dataPayload = response.data || [];
      let fetched = [];
      if (Array.isArray(dataPayload)) {
        const matches = dataPayload.filter(item => item && item.key === kvsKey);
        if (matches.length) {
          matches.forEach(entry => {
            const rows = parseEntryRows(entry);
            if (Array.isArray(rows) && rows.length)
              fetched = fetched.concat(rows);
          });
        }
      } else if (dataPayload && typeof dataPayload === 'object') {
        // Some APIs may return a single object or a { items: [...] } container
        if (Array.isArray(dataPayload.items)) {
          const matches = dataPayload.items.filter(
            item => item && item.key === kvsKey
          );
          matches.forEach(entry => {
            const rows = parseEntryRows(entry);
            if (Array.isArray(rows) && rows.length)
              fetched = fetched.concat(rows);
          });
        } else if (dataPayload.key === kvsKey) {
          fetched = parseEntryRows(dataPayload);
        }
      }
      setRows(fetched);

      if (!selectedDate) {
        const dateKeys = Array.from(
          new Set(fetched.map(row => toDateKey(row.id2)).filter(v => v))
        );
        if (dateKeys.length) {
          const latest = dateKeys
            .map(k => ({ k, n: parseDateKeyToNumeric(k) ?? 0 }))
            .sort((a, b) => b.n - a.n)[0].k;
          setSelectedDate(latest);
        } else {
          setSelectedDate(todayIso());
        }
      }
    } catch (error) {
      notify.error('Unable to fetch dashboard KVS');
    } finally {
      setLoading(false);
    }
  }, [
    canOperate,
    fetchExtras,
    ixDashboard,
    kvsKey,
    notify,
    parseEntryRows,
    request,
    selectedDate
  ]);

  useEffect(() => {
    if (!canOperate) return;
    if (lastFetchSignature.current === fetchSignature) return;
    lastFetchSignature.current = fetchSignature;
    fetchRows();
  }, [canOperate, fetchRows, fetchSignature]);

  const pageLabel = data?.id1?.label || 'Page';

  const openAddDialog = () => {
    if (!canOperate) {
      notify.error('Missing dashboard configuration.');
      return;
    }
    if (!selectedDate) {
      notify.error('Select a date first.');
      return;
    }
    if (!pageOptions.length) {
      notify.error('No pages available to assign.');
      return;
    }

    setDialogPageIds([]);
    setDialogError('');
    setDialogOpen(true);
  };

  const handleDialogSubmit = () => {
    if (!dialogPageIds || dialogPageIds.length === 0) {
      setDialogError('Select at least one page.');
      return;
    }

    const dateKey = toDateKey(selectedDate);
    if (!dateKey) {
      setDialogError('Select a valid date.');
      return;
    }
    setPendingPagesByDate(prev => {
      const next = { ...prev };
      const existing = new Set((next[dateKey] || []).map(String));
      dialogPageIds.forEach(pageId => existing.add(String(pageId)));
      next[dateKey] = Array.from(existing);
      return next;
    });
    setSelectedDate(dateKey);
    setDialogOpen(false);
  };

  const fetchPageOptions = useCallback(
    async payload => {
      const ixSubType = payload?.ixSubType ?? 0;
      if (!ixSubType) return;

      setPagesLoading(true);
      try {
        let response;
        for (const endpoint of SUB_ENDPOINTS) {
          response = await request.post(endpoint, payload);
          if (response?.success) break;
        }

        if (!response?.success) {
          notify.error(response?.errorMessage || 'Failed to load pages');
          return;
        }

        const rawList = Array.isArray(response.data?.sub_list)
          ? response.data.sub_list
          : Array.isArray(response.data)
          ? response.data
          : [];

        const normalized = rawList
          .map(item => ({
            value: String(item?.ixSub ?? item?.ixsub ?? item?.ixSUB ?? ''),
            label:
              item?.sSub ??
              item?.sSUB ??
              item?.name ??
              item?.sName ??
              String(item?.ixSub ?? '')
          }))
          .filter(option => option.value !== '' && option.value !== null);

        setPageOptions(normalized);
      } catch (error) {
        notify.error('Unable to load pages');
      } finally {
        setPagesLoading(false);
      }
    },
    [notify, request]
  );

  const lastPageFetchSignature = useRef('');

  useEffect(() => {
    if (!canOperate) return;
    const payload = pageFetchPayload || {};
    const signature = JSON.stringify(payload);
    if (!payload?.ixSubType) return;
    if (lastPageFetchSignature.current === signature) return;
    lastPageFetchSignature.current = signature;
    fetchPageOptions(payload);
  }, [canOperate, fetchPageOptions, pageFetchPayload]);

  const buildRowPayload = useCallback(
    row => {
      const payload = {};

      idFields.forEach(field => {
        const rawValue = row[field.key];
        
        // Always include the field in payload if it exists in row, even if empty/null
        if (typeof rawValue !== 'undefined') {
          if (rawValue === '' || rawValue === null) {
            // For required fields that are empty, send empty string or null
            payload[field.key] = rawValue;
          } else {
            const normalized =
              typeof rawValue === 'string' && rawValue.includes('-')
                ? parseDateInput(rawValue)
                : Number(rawValue);

            if (!Number.isNaN(normalized) && normalized !== null) {
              payload[field.key] = normalized;
            } else {
              // Keep original value if normalization fails
              payload[field.key] = rawValue;
            }
          }
        }
      });

      payload.value =
        row.value === '' || Number.isNaN(Number(row.value))
          ? 0
          : Number(row.value);

      if (row.id) {
        payload.id = row.id;
      }

      return payload;
    },
    [idFields]
  );

  const groupRowsByKey = useCallback(targetRows => {
    return targetRows.reduce((acc, row) => {
      if (!row.key) return acc;
      if (!acc[row.key]) acc[row.key] = [];
      acc[row.key].push(row);
      return acc;
    }, {});
  }, []);

  const composeKeyPayload = useCallback(
    (keyRows = []) => {
      if (!keyRows.length) return null;

      if (keyRows.length === 1) {
        return {
          ...buildRowPayload(keyRows[0]),
          ...saveExtras
        };
      }

      return keyRows.map(row => ({
        ...buildRowPayload(row),
        ...saveExtras
      }));
    },
    [saveExtras]
  );

  const persistKeyRows = useCallback(
    async (key, keyRows) => {
      if (!canOperate) return false;

      const keyPayload = composeKeyPayload(keyRows);
      if (!keyPayload) return false;

      const payload = {
        [key]: keyPayload
      };

      try {
        const response = await request.post(
          dashboardEndpoints.saveKvsItems(ixDashboard),
          payload
        );

        if (!response.success) {
          notify.error(response.errorMessage || 'Failed to save dashboard KVS');
          return false;
        }

        return true;
      } catch (error) {
        notify.error('Unable to save dashboard KVS');
        return false;
      }
    },
    [canOperate, composeKeyPayload, ixDashboard, notify, request]
  );

  const rowsByDate = useMemo(() => {
    return rows.reduce((acc, row) => {
      const dateKey = toDateKey(row.id2);
      if (!dateKey) return acc;
      if (!acc[dateKey]) acc[dateKey] = [];
      acc[dateKey].push(row);
      return acc;
    }, {});
  }, [rows]);

  useEffect(() => {
    if (selectedDate) return;
    const keys = Object.keys(rowsByDate);
    if (keys.length) {
      const latest = keys
        .map(k => ({ k, n: parseDateKeyToNumeric(k) ?? 0 }))
        .sort((a, b) => b.n - a.n)[0].k;
      setSelectedDate(latest);
    } else {
      setSelectedDate(todayIso());
    }
  }, [rowsByDate, selectedDate]);

  const rowsForSelectedDate = useMemo(() => rowsByDate[selectedDate] || [], [
    rowsByDate,
    selectedDate
  ]);

  const normalizedRows = useMemo(() => {
    const pendingPages = pendingPagesByDate[selectedDate] || [];
    const pageSet = new Set(
      rowsForSelectedDate
        .map(row => String(row.id1))
        .concat(pendingPages.map(String))
    );

    const pageArray = Array.from(pageSet);
    const regularRows = pageArray.map(pageId => {
      const rowEntries = rowsForSelectedDate.filter(
        r => String(r.id1) === pageId
      );
      const base = {
        id: pageId,
        pageId,
        pageLabel:
          pageOptions.find(opt => String(opt.value) === pageId)?.label || pageId
      };
      const hourValues = hourRange.reduce((acc, hour) => {
        const entry = rowEntries.find(r => Number(r.id3) === hour);
        if (entry) {
          acc[`hour_${hour}`] = entry.value;
          acc[`hour_${hour}__meta`] = entry;
        } else {
          acc[`hour_${hour}`] = '';
        }
        return acc;
      }, {});

      // Calculate total per page (row total)
      const total = hourRange.reduce((sum, hour) => {
        const value = hourValues[`hour_${hour}`];
        return (
          sum +
          (value !== '' && !Number.isNaN(Number(value)) ? Number(value) : 0)
        );
      }, 0);

      return {
        ...base,
        totalPerColumn: total,
        ...hourValues
      };
    });

    // Add summary rows if there are regular rows
    if (regularRows.length > 0) {
      // Calculate grand total across all pages and hours
      const grandTotal = regularRows.reduce(
        (sum, row) => sum + (row.totalPerColumn || 0),
        0
      );

      // Build a summary row where each hour column contains the grand total
      const totalPerHourRow = {
        id: 'total-per-hour',
        pageId: 'total-per-hour',
        pageLabel: 'Total Per Hour',
        totalPerColumn: grandTotal,
        ...hourRange.reduce((acc, hour) => {
          acc[`hour_${hour}`] = grandTotal;
          return acc;
        }, {})
      };

      return [...regularRows, totalPerHourRow];
    }

    return regularRows;
  }, [
    pageOptions,
    pendingPagesByDate,
    rowsForSelectedDate,
    selectedDate,
    hourRange
  ]);

  const handleDeleteRow = useCallback(
    async row => {
      if (!selectedDate) {
        notify.error('Select a date first.');
        return;
      }

      if (
        !window.confirm(
          `Are you sure you want to delete the row for "${row.pageLabel}"?`
        )
      ) {
        return;
      }

      setLoading(true);
      try {
        const existingEntries = rows.filter(item => {
          const samePage = String(item.id1) === String(row.pageId);
          const sameDate = toDateKey(item.id2) === selectedDate;
          return samePage && sameDate && item.id3;
        });


        if (existingEntries.length === 0) {
          notify.error('No data found to delete for this page and date');
          return;
        }

        const deletePromises = existingEntries.map(entry => {
          const payload = {
            [kvsKey]: {
              id1: entry.id1,
              id2: entry.id2,
              id3: entry.id3
            }
          };
          
          
          return request.post(
            dashboardEndpoints.kvsDelete(ixDashboard),
            payload
          );
        });

        // Execute all delete requests
        const responses = await Promise.all(deletePromises);
        
        // Check if any failed
        const hasFailure = responses.some(response => !response.success);
        if (hasFailure) {
          const failedResponse = responses.find(r => !r.success);
          notify.error(failedResponse?.errorMessage || 'Failed to delete row');
          return;
        }

        // Remove all entries for this page and date since we deleted all hours
        setRows(prev =>
          prev.filter(item => {
            const samePage = String(item.id1) === String(row.pageId);
            const sameDate = toDateKey(item.id2) === selectedDate;
            // Keep items that don't match both page and date
            return !(samePage && sameDate);
          })
        );

        notify.success('Row deleted successfully');
      } catch (error) {
        notify.error('Unable to delete row');
      } finally {
        setLoading(false);
      }
    },
    [selectedDate, notify, request, ixDashboard, kvsKey]
  );

  const handleExport = () => {
    if (!normalizedRows.length) {
      notify.error('No data to export');
      return;
    }

    // Filter out summary rows for export
    const regularRows = normalizedRows.filter(
      row => row.id !== 'total-per-hour'
    );

    const exportData = regularRows.map(row => {
      const exportRow = {
        Page: row.pageLabel,
        'Total Per Column': row.totalPerColumn || '0'
      };

      hourRange.forEach(hour => {
        const value = row[`hour_${hour}`];
        exportRow[`${hour}:00`] = value !== '' ? value : '0';
      });

      return exportRow;
    });

    // Add summary rows to export
    const totalPerHourData = {};
    // Use grand total for each hour column in the export summary
    const grandTotalForExport = regularRows.reduce(
      (sum, row) => sum + (row.totalPerColumn || 0),
      0
    );
    hourRange.forEach(hour => {
      totalPerHourData[`${hour}:00`] = grandTotalForExport;
    });
    totalPerHourData['Page'] = 'Total Per Hour';
    totalPerHourData['Total Per Column'] = grandTotalForExport;

    const finalExportData = [...exportData, totalPerHourData];

    const fields = {
      Page: 'Page',
      'Total Per Column': 'Total Per Column',
      ...hourRange.reduce((acc, hour) => {
        acc[`${hour}:00`] = `${hour}:00`;
        return acc;
      }, {})
    };

    const filename = `Ads Spent Monitoring-${selectedDate || 'all-dates'}`;
    const header = `${data?.caption || 'Dashboard KVS'} - ${selectedDate ||
      'All Dates'}`;

    exportCsvV2({
      data: finalExportData,
      fields,
      filename,
      header
    });

    notify.success('Export completed successfully');
  };

  const columns = useMemo(() => {
    const base = [
      {
        field: 'pageLabel',
        headerName: 'Page',
        width: 240,
        sticky: freezePane ? 'left' : null,
        headerStyle: { fontWeight: 'bold', fontSize: '1.1rem' },
        renderCell: params => {
          if (params.row.id === 'total-per-hour') {
            return (
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                Overall Total
              </Typography>
            );
          }
          return params.value;
        }
      },
      {
        field: 'totalPerColumn',
        headerName: 'Total Per Page',
        width: 140,
        type: 'number',
        editable: false,
        sticky: freezePane ? 'left' : null,
        align: 'right',
        headerAlign: 'right',
        headerStyle: { fontWeight: 'bold', fontSize: '1.1rem' },
        valueFormatter: params => {
          const v = params?.value;
          if (v === '' || v === null || typeof v === 'undefined') return '0';
          const num = Number(v);
          if (Number.isNaN(num)) return '0';
          const isDecimal =
            String(data?.type || '').toLowerCase() === 'decimal';
          return isDecimal ? numeral(num).format('0,0.00') : String(num);
        },
        renderCell: params => {
          if (params.row.id === 'total-per-hour') {
            return <Typography variant="subtitle2">{params.value}</Typography>;
          }
          return params.value;
        }
      }
    ];

    const isDecimal = String(data?.type || '').toLowerCase() === 'decimal';

    const hourColumns = hourRange.map(hour => ({
      field: `hour_${hour}`,
      headerName: `${hour}:00`,
      width: 140,
      type: 'number',
      editable: true,
      align: isDecimal ? 'right' : 'center',
      headerAlign: isDecimal ? 'right' : 'center',
      headerStyle: { fontWeight: 'bold', fontSize: '1.1rem' },
      valueFormatter: params => {
        const v = params?.value;
        if (v === '' || v === null || typeof v === 'undefined') return v;
        const num = Number(v);
        if (Number.isNaN(num)) return v;
        return isDecimal ? numeral(num).format('0,0.00') : String(num);
      },
      renderCell: params => {
        if (params.row.id === 'total-per-hour') {
          const v = params.value ?? 0;
          return (
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              {isDecimal ? numeral(Number(v)).format('0,0.00') : String(v)}
            </Typography>
          );
        }

        return params.value;
      }
    }));

    // Add actions column at the end
    const actionsColumn = {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      headerStyle: { fontWeight: 'bold', fontSize: '1.1rem' },
      renderCell: params => {
        if (params.row.id === 'total-per-hour') {
          return null; // No actions for summary rows
        }
        return (
          <IconButton
            size="small"
            onClick={() => handleDeleteRow(params.row)}
            disabled={loading}
            title="Delete row"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        );
      }
    };

    return base.concat(hourColumns).concat([actionsColumn]);
  }, [data?.type, hourRange, freezePane, loading, handleDeleteRow]);

  const handleCellEdit = async params => {
    const { field, value, row } = params;
    if (!field.startsWith('hour_')) return;

    if (!selectedDate) {
      notify.error('Select a date first.');
      return;
    }

    const hour = Number(field.replace('hour_', ''));
    if (Number.isNaN(hour)) return;

    const numericValue = Number(value);
    if (value !== '' && Number.isNaN(numericValue)) {
      notify.error('Value must be numeric.');
      return;
    }

    const meta = row[`${field}__meta`];

    const payloadRow = {
      tempId: uuidv4(),
      key: kvsKey,
      isPersisted: true,
      id1: row.pageId,
      id2: parseDateKeyToNumeric(selectedDate),
      id3: hour,
      value: value === '' ? 0 : numericValue
    };

    if (meta?.id) {
      payloadRow.id = meta.id;
    }

    const success = await persistKeyRows(kvsKey, [payloadRow]);

    if (success) {
      setRows(prev => {
        let updated = false;
        const next = prev.map(item => {
          const samePage = String(item.id1) === String(row.pageId);
          const sameHour = Number(item.id3) === hour;
          const sameDate = toDateKey(item.id2) === selectedDate;
          if (samePage && sameHour && sameDate) {
            updated = true;
            return {
              ...item,
              value: value === '' ? 0 : numericValue,
              id2: payloadRow.id2
            };
          }
          return item;
        });

        if (!updated) {
          next.push({
            tempId: uuidv4(),
            key: kvsKey,
            isPersisted: true,
            id1: row.pageId,
            id2: payloadRow.id2,
            id3: hour,
            value: value === '' ? 0 : numericValue,
            id: meta?.id
          });
        }

        return next;
      });

      notify.success('Saved');
    }
  };

  useEffect(() => {
    setPendingPagesByDate(prev => {
      const next = { ...prev };
      let changed = false;

      Object.keys(next).forEach(dateKey => {
        const remaining = next[dateKey].filter(
          pageId =>
            !rows.some(
              row =>
                toDateKey(row.id2) === dateKey && String(row.id1) === pageId
            )
        );

        if (!remaining.length) {
          delete next[dateKey];
          changed = true;
        } else if (remaining.length !== next[dateKey].length) {
          next[dateKey] = remaining;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [rows]);

  const selectedDateInputValue = useMemo(() => {
    if (isIsoDate(selectedDate)) return selectedDate;
    const iso = formatDateInput(selectedDate);
    return iso || '';
  }, [selectedDate]);

  const handleDatePickerChange = useCallback(event => {
    const nextValue = event?.target?.value || '';
    setSelectedDate(nextValue);
  }, []);

  if (!canOperate) {
    return (
      <Paper className={classes.root}>
        <Typography color="textSecondary">
          Dashboard KVS requires both an ixDashboard and KVS key configuration.
        </Typography>
      </Paper>
    );
  }

  const showEmptyState = Boolean(
    selectedDate && !loading && !normalizedRows.length
  );

  return (
    <Paper className={classes.root}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Typography variant="h5">
            {data?.caption || 'Dashboard KVS'}
          </Typography>
          {data?.description && (
            <Typography variant="body2" color="textSecondary">
              {data.description}
            </Typography>
          )}
          <Box className={classes.dateControls}>
            <Box className={classes.headerActions}>
              <Button
                variant="contained"
                size="small"
                startIcon={<RefreshIcon />}
                onClick={fetchRows}
                disabled={loading}
                color="primary"
              >
                Refresh
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={openAddDialog}
                disabled={loading || pagesLoading}
                color="primary"
              >
                {`Add ${pageLabel}`}
              </Button>
            </Box>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <Box className={classes.dateControls}>
            <Button
              variant="contained"
              size="small"
              startIcon={<GetAppIcon />}
              onClick={handleExport}
              disabled={loading || !normalizedRows.length}
              color="primary"
            >
              Export
            </Button>
            <Box minWidth={220} maxWidth={260}>
              <SingleDatePicker
                label="Date"
                value={selectedDateInputValue}
                onChange={handleDatePickerChange}
                name="dashboard-kvs-date"
                fullWidth
                disabled={loading}
                helperText={
                  !isIsoDate(selectedDate) && selectedDate
                    ? `Saved key: ${selectedDate}`
                    : ''
                }
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider />
      <Box mt={2}>
        {!selectedDate ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              Select or add a date to view entries.
            </Typography>
          </Box>
        ) : showEmptyState ? (
          <Box className={classes.emptyState}>
            <Typography variant="body2">
              No entries yet for this date. Use "Add Page" to start populating
              values.
            </Typography>
          </Box>
        ) : (
          <DataTable
            key="datatable-20-rows"
            columns={columns}
            data={normalizedRows}
            rowIDKey="pageId"
            isLoading={loading}
            onCellEdit={handleCellEdit}
            maxHeight={420}
            paginate={true}
            rowsPerPageOptions={[10, 20, 50, 100]}
            pageSize={20}
          />
        )}
      </Box>
      <AddPageDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleDialogSubmit}
        pageLabel={pageLabel}
        dialogPageIds={dialogPageIds}
        setDialogPageIds={setDialogPageIds}
        pageOptions={pageOptions}
        pagesLoading={pagesLoading}
        selectedDate={selectedDate}
        dialogError={dialogError}
      />
    </Paper>
  );
};

DashboardKVSField.propTypes = {
  data: PropTypes.object
};

export default DashboardKVSField;
